// PREZENCE — HR Leave Approvals (Advanced — same as AdminLeaves)
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api/client';
import toast from 'react-hot-toast';

const getInitials = n => (n||'').split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase();
const TABS = ['pending','approved','rejected','all'];

export default function LeaveApprovals() {
  const [leaves, setLeaves]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [activeTab, setActiveTab]   = useState('pending');
  const [reviewing, setReviewing]   = useState(null);
  const [notes, setNotes]           = useState('');
  const [processing, setProcessing] = useState(false);
  const [empStats, setEmpStats]     = useState(null);

  const fetchLeaves = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/leaves/list-all?status=${activeTab}`);
      setLeaves(res.data.data || []);
    } catch { toast.error('Failed to load leave requests'); }
    finally { setLoading(false); }
  }, [activeTab]);

  useEffect(() => { fetchLeaves(); }, [fetchLeaves]);

  const openReview = async (leave) => {
    setReviewing(leave);
    setNotes('');
    try {
      const res = await api.get(`/leaves/stats?user_id=${leave.users?.id || leave.user_id}`);
      setEmpStats(res.data);
    } catch { setEmpStats(null); }
  };

  const handleReview = async (status) => {
    if (!reviewing) return;
    if (status === 'rejected' && !notes.trim()) {
      toast.error('Please provide a reason for rejection'); return;
    }
    setProcessing(true);
    try {
      await api.patch(`/leaves/${reviewing.id}/review`, { status, review_notes: notes });
      toast.success(`Leave ${status} ✅`);
      setReviewing(null); setNotes(''); setEmpStats(null);
      fetchLeaves();
    } catch (e) {
      toast.error(e.response?.data?.error || `Failed to ${status}`);
    } finally { setProcessing(false); }
  };

  const statusBadge = s => ({ approved:'badge-green', rejected:'badge-red', pending:'badge-amber', cancelled:'badge-gray' })[s]||'badge-gray';

  return (
    <div style={{ animation:'fadeIn 0.4s ease' }}>
      <div className="page-header">
        <h1 className="page-title">Leave Approvals</h1>
        <p className="page-sub">Review, approve or reject employee leave applications</p>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:8, marginBottom:20, borderBottom:'1px solid rgba(56,189,248,0.08)', paddingBottom:12 }}>
        {TABS.map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)} style={{
            background: activeTab===tab ? 'rgba(99,220,188,0.12)' : 'transparent',
            border: `1px solid ${activeTab===tab ? 'rgba(99,220,188,0.35)' : 'transparent'}`,
            color: activeTab===tab ? '#63dcbc' : '#64748b',
            borderRadius:8, padding:'7px 18px', cursor:'pointer',
            fontSize:12, fontWeight:600, fontFamily:"'DM Sans',sans-serif",
            textTransform:'capitalize', transition:'all 0.2s',
          }}>
            {tab === 'all' ? 'All Leaves' : tab.charAt(0).toUpperCase()+tab.slice(1)}
            {tab === 'pending' && leaves.filter(l=>l.status==='pending').length > 0 && activeTab==='pending' && (
              <span style={{ marginLeft:6, background:'#f59e0b', color:'#05090f', borderRadius:20, padding:'1px 7px', fontSize:10, fontWeight:800 }}>
                {leaves.length}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="card">
        <div className="card-header">
          <span className="card-title">{activeTab==='all'?'ALL APPLICATIONS':`${activeTab.toUpperCase()} REQUESTS`}</span>
          <span style={{ fontSize:12, color:'#475569' }}>{leaves.length} records</span>
        </div>

        {loading ? (
          <div style={{ display:'flex', justifyContent:'center', padding:48 }}><div className="spinner" style={{width:28,height:28}} /></div>
        ) : leaves.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">{activeTab==='pending'?'✅':'📋'}</div>
            <div className="empty-state-text">{activeTab==='pending'?'All caught up! No pending requests.':'No records found.'}</div>
          </div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            {leaves.map(leave => (
              <div key={leave.id} style={{
                background:'rgba(255,255,255,0.02)',
                border:`1px solid ${leave.status==='pending'?'rgba(245,158,11,0.2)':leave.status==='approved'?'rgba(34,197,94,0.15)':'rgba(56,189,248,0.07)'}`,
                borderRadius:12, padding:'16px 20px',
              }}>
                <div style={{ display:'flex', alignItems:'flex-start', gap:14, flexWrap:'wrap' }}>
                  <div className="avatar avatar-md">{getInitials(leave.users?.full_name||leave.user?.full_name)}</div>
                  <div style={{ flex:1, minWidth:180 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4, flexWrap:'wrap' }}>
                      <span style={{ fontSize:14, fontWeight:700, color:'#e2e8f0' }}>{leave.users?.full_name||leave.user?.full_name||'Employee'}</span>
                      <span className={`badge ${statusBadge(leave.status)}`}>{leave.status.charAt(0).toUpperCase()+leave.status.slice(1)}</span>
                    </div>
                    <div style={{ fontSize:11, color:'#475569', marginBottom:10 }}>{leave.users?.department||leave.user?.department}</div>
                    <div style={{ display:'flex', gap:16, flexWrap:'wrap', marginBottom:8 }}>
                      <div>
                        <div style={{ fontSize:9, color:'#334155', fontFamily:"'JetBrains Mono'", marginBottom:2 }}>LEAVE TYPE</div>
                        <div style={{ display:'flex', alignItems:'center', gap:5 }}>
                          <div style={{ width:7,height:7,borderRadius:'50%',background:leave.leave_types?.color||'#63dcbc' }} />
                          <span style={{ fontSize:12, fontWeight:600, color:'#94a3b8' }}>{leave.leave_types?.name||leave.leave_type?.name||'—'}</span>
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize:9, color:'#334155', fontFamily:"'JetBrains Mono'", marginBottom:2 }}>DATES</div>
                        <div style={{ fontSize:12, fontWeight:600, color:'#94a3b8' }}>
                          {new Date(leave.from_date).toLocaleDateString('en-IN',{day:'numeric',month:'short'})}
                          {leave.from_date!==leave.to_date && ` → ${new Date(leave.to_date).toLocaleDateString('en-IN',{day:'numeric',month:'short'})}`}
                          <span style={{ color:'#63dcbc', marginLeft:6, fontFamily:"'JetBrains Mono'" }}>({leave.total_days}d)</span>
                        </div>
                      </div>
                    </div>
                    <div style={{ padding:'8px 12px', background:'rgba(255,255,255,0.02)', borderRadius:8, borderLeft:'2px solid rgba(99,220,188,0.2)', marginBottom: leave.review_notes ? 8 : 0 }}>
                      <div style={{ fontSize:9, color:'#334155', fontFamily:"'JetBrains Mono'", marginBottom:2 }}>REASON</div>
                      <div style={{ fontSize:12, color:'#94a3b8' }}>{leave.reason}</div>
                    </div>
                    {leave.review_notes && (
                      <div style={{ padding:'8px 12px', background:leave.status==='approved'?'rgba(34,197,94,0.06)':'rgba(239,68,68,0.06)', borderRadius:8, borderLeft:`3px solid ${leave.status==='approved'?'#22c55e':'#ef4444'}` }}>
                        <div style={{ fontSize:9, color:leave.status==='approved'?'#22c55e':'#ef4444', fontFamily:"'JetBrains Mono'", marginBottom:2 }}>HR NOTE</div>
                        <div style={{ fontSize:12, color:'#94a3b8' }}>{leave.review_notes}</div>
                      </div>
                    )}
                  </div>
                  {leave.status === 'pending' && (
                    <button className="btn btn-primary btn-sm" style={{ flexShrink:0 }} onClick={() => openReview(leave)}>Review</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Review modal */}
      {reviewing && (
        <div className="modal-overlay" onClick={() => { setReviewing(null); setEmpStats(null); }}>
          <div className="modal-box" style={{ maxWidth:580 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Review Leave Request</h2>
              <button className="modal-close" onClick={() => { setReviewing(null); setEmpStats(null); }}>✕</button>
            </div>

            <div style={{ padding:'14px 16px', background:'rgba(99,220,188,0.05)', border:'1px solid rgba(99,220,188,0.12)', borderRadius:10, marginBottom:18 }}>
              <div style={{ fontSize:14, fontWeight:700, color:'#e2e8f0', marginBottom:4 }}>{reviewing.users?.full_name||reviewing.user?.full_name}</div>
              <div style={{ fontSize:12, color:'#64748b' }}>
                {reviewing.leave_types?.name||reviewing.leave_type?.name} · {reviewing.total_days} day{reviewing.total_days>1?'s':''} ·{' '}
                {new Date(reviewing.from_date).toLocaleDateString('en-IN',{day:'numeric',month:'short'})} to{' '}
                {new Date(reviewing.to_date).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})}
              </div>
              <div style={{ fontSize:12, color:'#94a3b8', marginTop:6, fontStyle:'italic' }}>"{reviewing.reason}"</div>
            </div>

            {/* Employee stats table */}
            {empStats?.stats?.length > 0 && (
              <div style={{ marginBottom:18 }}>
                <div style={{ fontSize:10, color:'#475569', fontFamily:"'JetBrains Mono'", letterSpacing:'0.1em', marginBottom:8 }}>LEAVE STATEMENT — {empStats.year}</div>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:11 }}>
                  <thead>
                    <tr style={{ background:'rgba(56,189,248,0.06)', borderBottom:'1px solid rgba(56,189,248,0.1)' }}>
                      {['Type','Total','Availed','Balance','Applied'].map(h => (
                        <th key={h} style={{ padding:'6px 10px', textAlign:'left', color:'#475569', fontFamily:"'JetBrains Mono'", fontSize:9 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {empStats.stats.map((s, i) => {
                      const isCurrent = s.leave_type_id === reviewing.leave_type_id;
                      return (
                        <tr key={i} style={{ borderBottom:'1px solid rgba(255,255,255,0.04)', background:isCurrent?'rgba(99,220,188,0.05)':'transparent' }}>
                          <td style={{ padding:'6px 10px' }}>
                            <div style={{ display:'flex', alignItems:'center', gap:5 }}>
                              <div style={{ width:6,height:6,borderRadius:'50%',background:s.color }} />
                              <span style={{ color:isCurrent?'#63dcbc':'#94a3b8', fontWeight:isCurrent?700:400, fontSize:11 }}>{s.name}</span>
                            </div>
                          </td>
                          <td style={{ padding:'6px 10px', color:'#94a3b8', fontFamily:"'JetBrains Mono'" }}>{s.allocated}</td>
                          <td style={{ padding:'6px 10px', color:'#f59e0b', fontFamily:"'JetBrains Mono'" }}>{s.availed}</td>
                          <td style={{ padding:'6px 10px', fontFamily:"'JetBrains Mono'", fontWeight:700, color:s.balance<=2?'#ef4444':s.balance<=5?'#f59e0b':'#22c55e' }}>{s.balance}</td>
                          <td style={{ padding:'6px 10px', color:'#63dcbc', fontFamily:"'JetBrains Mono'" }}>{isCurrent?reviewing.total_days:'—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">NOTE TO EMPLOYEE <span style={{ color:'#ef4444' }}>*required for rejection</span></label>
              <textarea className="form-textarea" value={notes} onChange={e => setNotes(e.target.value)}
                placeholder={`e.g. "This leave cannot be granted as a client meeting is scheduled. Please reschedule."`}
                style={{ minHeight:80 }} />
            </div>

            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => { setReviewing(null); setEmpStats(null); }} disabled={processing}>Cancel</button>
              <button className="btn btn-danger" onClick={() => handleReview('rejected')} disabled={processing||!notes.trim()}>
                {processing ? <div className="spinner" style={{width:14,height:14}} /> : '✕ Not Approved'}
              </button>
              <button className="btn btn-primary" style={{ background:'linear-gradient(135deg,#22c55e,#16a34a)' }} onClick={() => handleReview('approved')} disabled={processing}>
                {processing ? <div className="spinner" style={{width:14,height:14}} /> : '✓ Approved'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
